import { useEffect, useMemo, useState } from 'react';
import { ROLE_LABELS, ROLE_COLORS, useUser } from '../UserContext';

const ACTIVITY_LABELS = {
  drive: 'Drive', bench: 'Bench', on_site: 'On Site',
  testing: 'Testing', admin: 'Admin', return_drive: 'Return Drive',
};

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || 'Request failed');
    return data;
  });
}

const ROLES = ['tech', 'field_engineer', 'rpm', 'design', 'management', 'admin', 'field_manager', 'director', 'managing_director'];
const CAREER_LEVELS = [
  { value: '', label: 'Unassigned' },
  { value: 'app_3', label: 'Telecom Apprentice III' },
  { value: 'app_2', label: 'Telecom Apprentice II' },
  { value: 'app_1', label: 'Telecom Apprentice I' },
  { value: 'tech_c2', label: 'Telecom Tech C2' },
  { value: 'tech_c1', label: 'Telecom Tech C1' },
  { value: 'tech_b', label: 'Telecom Tech B' },
  { value: 'tech_a', label: 'Telecom Tech A' },
  { value: 'tech_sr', label: 'Telecom Tech SR' },
];
const DEFAULT_SKILLS = ['radio_repair', 'transport', 'fiber_splice', 'equipment_install', 'testing', 'inspection'];

const emptyCreateForm = {
  name: '',
  email: '',
  password: '',
  role: 'tech',
  career_level: '',
  shift_start: '',
  shift_end: '',
  reporting_location_id: '',
  skillsText: '',
};

function formatCareer(value) {
  return CAREER_LEVELS.find(level => level.value === value)?.label || 'Unassigned';
}

function formatTime(value) {
  return value ? String(value).slice(0, 5) : '--:--';
}

function skillsToText(skills) {
  return Array.isArray(skills) ? skills.join(', ') : '';
}

function textToSkills(text) {
  return String(text || '')
    .split(',')
    .map(skill => skill.trim())
    .filter(Boolean);
}

function normalizeUserForEdit(user) {
  return {
    ...user,
    career_level: user.career_level || '',
    shift_start: user.shift_start ? user.shift_start.slice(0, 5) : '',
    shift_end: user.shift_end ? user.shift_end.slice(0, 5) : '',
    reporting_location_id: user.reporting_location_id || '',
    skillsText: skillsToText(user.skills),
  };
}

function fieldStyle(disabled = false) {
  return {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: disabled ? '#111827' : '#0f172a',
    border: '1px solid #334155',
    borderRadius: 6,
    color: disabled ? '#64748b' : '#e2e8f0',
    fontSize: 13,
    boxSizing: 'border-box',
  };
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function TimesheetPanel({ user, timesheet, loading, dateFrom, dateTo, onFromChange, onToChange, onLoad }) {
  const inputStyle = {
    padding: '7px 10px', backgroundColor: '#0f172a', border: '1px solid #334155',
    borderRadius: 6, color: '#e2e8f0', fontSize: 13,
  };

  const totals = timesheet.reduce((acc, row) => {
    acc.regular += row.regular_hours || 0;
    acc.ot += row.ot_hours || 0;
    acc.total += row.total_hours || 0;
    return acc;
  }, { regular: 0, ot: 0, total: 0 });

  const byActivity = timesheet.reduce((acc, row) => {
    const key = row.activity_type;
    if (!acc[key]) acc[key] = 0;
    acc[key] += row.total_hours || 0;
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>From</div>
          <input type="date" value={dateFrom} onChange={e => onFromChange(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>To</div>
          <input type="date" value={dateTo} onChange={e => onToChange(e.target.value)} style={inputStyle} />
        </div>
        <button onClick={onLoad} style={{ padding: '8px 14px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          Load
        </button>
      </div>

      {loading ? (
        <div style={{ color: '#64748b', fontSize: 13 }}>Loading timesheet…</div>
      ) : timesheet.length === 0 ? (
        <div style={{ border: '1px dashed #334155', borderRadius: 8, padding: 32, textAlign: 'center', color: '#64748b' }}>
          No time entries for {user.name} in this date range.
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
            {[
              ['Regular Hours', totals.regular.toFixed(2), '#22c55e'],
              ['Overtime Hours', totals.ot.toFixed(2), '#f59e0b'],
              ['Total Hours', totals.total.toFixed(2), '#3b82f6'],
            ].map(([label, value, color]) => (
              <div key={label} style={{ backgroundColor: '#111827', border: `1px solid ${color}33`, borderRadius: 7, padding: 12 }}>
                <div style={{ color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>{label}</div>
                <div style={{ color, fontSize: 20, fontWeight: 800, marginTop: 5 }}>{value}</div>
              </div>
            ))}
          </div>

          {Object.keys(byActivity).length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {Object.entries(byActivity).map(([type, hours]) => (
                <div key={type} style={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '7px 12px' }}>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>{ACTIVITY_LABELS[type] || type}: </span>
                  <span style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 700 }}>{hours.toFixed(2)}h</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#111827' }}>
                  {['Date', 'Work Order', 'Activity', 'Start', 'End', 'Regular', 'OT', 'Notes'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timesheet.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #111827' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#111827'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '9px 12px', color: '#e2e8f0' }}>{new Date(row.started_at).toLocaleDateString()}</td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{row.wo_number ? `WO ${row.wo_number}` : row.description?.slice(0, 24) || '--'}</td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{ACTIVITY_LABELS[row.activity_type] || row.activity_type}</td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8', fontFamily: 'monospace' }}>{new Date(row.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '9px 12px', color: row.ended_at ? '#94a3b8' : '#f59e0b', fontFamily: 'monospace' }}>
                      {row.ended_at ? new Date(row.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Open'}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#22c55e', fontWeight: 700 }}>{(row.regular_hours || 0).toFixed(2)}</td>
                    <td style={{ padding: '9px 12px', color: row.ot_hours > 0 ? '#f59e0b' : '#475569', fontWeight: row.ot_hours > 0 ? 700 : 400 }}>{(row.ot_hours || 0).toFixed(2)}</td>
                    <td style={{ padding: '9px 12px', color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.notes || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CalloutHistoryPanel({ user }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    api(`/callouts/history/${user.id}`)
      .then(rows => { setHistory(rows); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user?.id]);

  const STATUS_STYLE = {
    accepted:  { color: '#22c55e', bg: '#14532d22', border: '#22c55e44' },
    rejected:  { color: '#ef4444', bg: '#450a0a22', border: '#ef444444' },
    cancelled: { color: '#64748b', bg: '#1e293b',   border: '#33415544' },
    pending:   { color: '#f59e0b', bg: '#f59e0b22', border: '#f59e0b44' },
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: '12px 18px', border: '1px solid #334155', minWidth: 120 }}>
          <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>YTD Callouts</div>
          <div style={{ color: '#f8fafc', fontSize: 24, fontWeight: 800, marginTop: 4 }}>{user?.callout_count_ytd ?? '—'}</div>
        </div>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 8, padding: '12px 18px', border: '1px solid #334155', minWidth: 160 }}>
          <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Last Callout</div>
          <div style={{ color: '#f8fafc', fontSize: 15, fontWeight: 700, marginTop: 4 }}>
            {user?.last_callout_at ? new Date(user.last_callout_at).toLocaleDateString() : '—'}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#64748b', fontSize: 13 }}>Loading callout history…</div>
      ) : history.length === 0 ? (
        <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 30, border: '1px dashed #334155', borderRadius: 8 }}>
          No callout history.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Date', 'Work Order', 'Priority', 'Status', 'Response Reason', 'Response Time', 'Requested By'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map(c => {
              const ss = STATUS_STYLE[c.status] || STATUS_STYLE.cancelled;
              const mins = c.response_minutes != null ? Math.round(c.response_minutes) : null;
              return (
                <tr key={c.id} style={{ borderBottom: '1px solid #0f172a' }}>
                  <td style={{ padding: '9px 10px', color: '#94a3b8' }}>{new Date(c.initiated_at).toLocaleDateString()}</td>
                  <td style={{ padding: '9px 10px', color: '#e2e8f0', fontWeight: 700 }}>{c.work_order_number || `WO-${c.work_item_id}`}</td>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4,
                      backgroundColor: `${c.priority === 'critical' ? '#ef4444' : '#f97316'}22`,
                      color: c.priority === 'critical' ? '#ef4444' : '#f97316',
                      border: `1px solid ${c.priority === 'critical' ? '#ef444444' : '#f9731644'}` }}>
                      {c.priority}
                    </span>
                  </td>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                      backgroundColor: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={{ padding: '9px 10px', color: '#64748b', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.response_reason || '—'}
                  </td>
                  <td style={{ padding: '9px 10px', color: '#94a3b8' }}>
                    {mins != null ? (mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`) : '—'}
                  </td>
                  <td style={{ padding: '9px 10px', color: '#94a3b8' }}>{c.initiated_by_name || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Per-user Org / PIN panel (shown in the user detail "Org / PIN" tab) ────────

function OrgUserPanel({ user, locations, onSaved }) {
  const [opcos, setOpcos] = useState([]);
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ opco_id: '', team_id: '', bench_location_id: '' });
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api('/orgs/opcos').then(rows => setOpcos(Array.isArray(rows) ? rows : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.opco_id) { setTeams([]); return; }
    api(`/orgs/teams?opco_id=${form.opco_id}`).then(rows => setTeams(Array.isArray(rows) ? rows : [])).catch(() => {});
  }, [form.opco_id]);

  useEffect(() => {
    if (!user) return;
    setForm({
      opco_id:          user.opco_id          != null ? String(user.opco_id)          : '',
      team_id:          user.team_id          != null ? String(user.team_id)          : '',
      bench_location_id: user.bench_location_id != null ? String(user.bench_location_id) : '',
    });
  }, [user]);

  const inpS = {
    padding: '7px 10px', backgroundColor: '#0f172a', border: '1px solid #334155',
    borderRadius: 6, color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  const saveOrg = async () => {
    setErr(''); setStatus('');
    try {
      const updated = await api(`/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ opco_id: form.opco_id || null, team_id: form.team_id || null, bench_location_id: form.bench_location_id || null }),
      });
      onSaved(updated);
      setStatus('Saved');
    } catch (e) { setErr(e.message); }
  };

  const setPin_ = async () => {
    setErr(''); setStatus('');
    if (pin.length < 4) { setErr('PIN must be at least 4 characters'); return; }
    try {
      await api(`/users/${user.id}/jha-pin`, { method: 'POST', body: JSON.stringify({ pin }) });
      setPin('');
      setStatus('PIN set');
    } catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <section style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ padding: '10px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800 }}>Org Assignment</div>
        <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ display: 'block' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>OPCO</div>
            <select value={form.opco_id} onChange={e => setForm(p => ({ ...p, opco_id: e.target.value, team_id: '' }))} style={inpS}>
              <option value="">— Unassigned —</option>
              {opcos.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Team</div>
            <select value={form.team_id} onChange={e => setForm(p => ({ ...p, team_id: e.target.value }))} style={inpS} disabled={!form.opco_id}>
              <option value="">— Unassigned —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'block', gridColumn: '1 / -1' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>Bench / Home Location</div>
            <select value={form.bench_location_id} onChange={e => setForm(p => ({ ...p, bench_location_id: e.target.value }))} style={inpS}>
              <option value="">— Unassigned —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
        </div>
        {(err || status) && <div style={{ color: err ? '#f87171' : '#22c55e', padding: '0 14px 10px', fontSize: 13, fontWeight: 700 }}>{err || status}</div>}
        <div style={{ padding: '0 14px 14px' }}>
          <button onClick={saveOrg} style={{ padding: '8px 18px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>Save Org</button>
        </div>
      </section>

      <section style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800 }}>JHA PIN</div>
        <div style={{ padding: 14 }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>Set or reset this user's JHA signing PIN (min 4 characters).</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="New PIN" style={{ ...inpS, width: 160, flex: 'none' }} />
            <button onClick={setPin_} style={{ padding: '8px 16px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>Set PIN</button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Org Admin (OPCOs + Teams) ──────────────────────────────────────────────────

function OrgAdmin({ users }) {
  const [opcos, setOpcos] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpco, setSelectedOpco] = useState(null);

  // create OPCO
  const [showNewOpco, setShowNewOpco] = useState(false);
  const [newOpcoName, setNewOpcoName] = useState('');
  const [newOpcoDir, setNewOpcoDir] = useState('');

  // edit OPCO
  const [editOpcoId, setEditOpcoId] = useState(null);
  const [editOpcoName, setEditOpcoName] = useState('');
  const [editOpcoDir, setEditOpcoDir] = useState('');
  const [deleteOpcoConfirm, setDeleteOpcoConfirm] = useState(null); // opco id awaiting confirm

  // create team
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamFm, setNewTeamFm] = useState('');

  // edit team
  const [editTeamId, setEditTeamId] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamFm, setEditTeamFm] = useState('');
  const [deleteTeamConfirm, setDeleteTeamConfirm] = useState(null); // team id awaiting confirm

  const [err, setErr] = useState('');
  const [status, setStatus] = useState('');

  const load = () => {
    setLoading(true);
    api('/orgs/opcos')
      .then(rows => { setOpcos(Array.isArray(rows) ? rows : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const loadTeams = (opco) => {
    if (!opco) { setTeams([]); return; }
    api(`/orgs/teams?opco_id=${opco.id}`)
      .then(rows => setTeams(Array.isArray(rows) ? rows : []))
      .catch(() => setTeams([]));
  };

  useEffect(() => { loadTeams(selectedOpco); }, [selectedOpco]);

  // ── OPCO actions ────────────────────────────────────────────────
  const createOpco = async () => {
    if (!newOpcoName.trim()) return;
    setErr('');
    try {
      const created = await api('/orgs/opcos', { method: 'POST', body: JSON.stringify({ name: newOpcoName, director_id: newOpcoDir || undefined }) });
      setOpcos(prev => [...prev, created]);
      setShowNewOpco(false); setNewOpcoName(''); setNewOpcoDir('');
      setStatus('OPCO created');
    } catch (e) { setErr(e.message); }
  };

  const startEditOpco = (o) => {
    setEditOpcoId(o.id); setEditOpcoName(o.name); setEditOpcoDir(o.director_id || '');
    setDeleteOpcoConfirm(null);
  };

  const saveOpco = async () => {
    setErr('');
    try {
      const updated = await api(`/orgs/opcos/${editOpcoId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: editOpcoName.trim(), director_id: editOpcoDir || null }),
      });
      setOpcos(prev => prev.map(o => o.id === editOpcoId ? { ...o, ...updated } : o));
      if (selectedOpco?.id === editOpcoId) setSelectedOpco(prev => ({ ...prev, ...updated }));
      setEditOpcoId(null);
      setStatus('OPCO saved');
    } catch (e) { setErr(e.message); }
  };

  const deleteOpco = async (id) => {
    setErr('');
    try {
      await api(`/orgs/opcos/${id}`, { method: 'DELETE' });
      setOpcos(prev => prev.filter(o => o.id !== id));
      if (selectedOpco?.id === id) { setSelectedOpco(null); setTeams([]); }
      setDeleteOpcoConfirm(null);
      setStatus('OPCO deleted');
    } catch (e) { setErr(e.message); setDeleteOpcoConfirm(null); }
  };

  // ── Team actions ────────────────────────────────────────────────
  const createTeam = async () => {
    if (!selectedOpco || !newTeamName.trim()) return;
    setErr('');
    try {
      const created = await api('/orgs/teams', { method: 'POST', body: JSON.stringify({ name: newTeamName, opco_id: selectedOpco.id, field_manager_id: newTeamFm || undefined }) });
      setTeams(prev => [...prev, created]);
      setShowNewTeam(false); setNewTeamName(''); setNewTeamFm('');
      setStatus('Team created');
    } catch (e) { setErr(e.message); }
  };

  const startEditTeam = (t) => {
    setEditTeamId(t.id); setEditTeamName(t.name); setEditTeamFm(t.field_manager_id || '');
    setDeleteTeamConfirm(null);
  };

  const saveTeam = async () => {
    setErr('');
    try {
      const updated = await api(`/orgs/teams/${editTeamId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: editTeamName.trim(), field_manager_id: editTeamFm || null }),
      });
      setTeams(prev => prev.map(t => t.id === editTeamId ? { ...t, ...updated } : t));
      setEditTeamId(null);
      setStatus('Team saved');
    } catch (e) { setErr(e.message); }
  };

  const deleteTeam = async (id) => {
    setErr('');
    try {
      await api(`/orgs/teams/${id}`, { method: 'DELETE' });
      setTeams(prev => prev.filter(t => t.id !== id));
      setDeleteTeamConfirm(null);
      setStatus('Team deleted');
    } catch (e) { setErr(e.message); setDeleteTeamConfirm(null); }
  };

  const directors = users.filter(u => ['director', 'managing_director', 'admin'].includes(u.role));
  const managers  = users.filter(u => ['field_manager', 'admin', 'rpm'].includes(u.role));

  const inpS = {
    padding: '7px 10px', backgroundColor: '#0f172a', border: '1px solid #334155',
    borderRadius: 6, color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  const btnSm = (bg, color, border) => ({
    padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    borderRadius: 5, border: border || 'none', backgroundColor: bg, color,
  });

  return (
    <div style={{ maxWidth: 900 }}>
      {(err || status) && (
        <div style={{ color: err ? '#f87171' : '#22c55e', marginBottom: 12, fontSize: 13, fontWeight: 700 }}>
          {err || status}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* ── OPCOs ─────────────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 15 }}>OPCOs</div>
            <button onClick={() => { setShowNewOpco(o => !o); setEditOpcoId(null); }}
              style={{ padding: '6px 12px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              + OPCO
            </button>
          </div>

          {/* New OPCO form */}
          {showNewOpco && (
            <div style={{ border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 12, backgroundColor: '#111827' }}>
              <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Name</div>
                  <input value={newOpcoName} onChange={e => setNewOpcoName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && createOpco()} style={inpS} autoFocus />
                </div>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Director</div>
                  <select value={newOpcoDir} onChange={e => setNewOpcoDir(e.target.value)} style={inpS}>
                    <option value="">— None —</option>
                    {directors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowNewOpco(false)} style={{ flex: 1, padding: 8, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                <button onClick={createOpco} style={{ flex: 2, padding: 8, backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>Create</button>
              </div>
            </div>
          )}

          {/* OPCO list */}
          {loading ? <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div> : opcos.length === 0 ? (
            <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>No OPCOs yet.</div>
          ) : opcos.map(o => (
            <div key={o.id} style={{ marginBottom: 6 }}>
              {/* OPCO card */}
              <div style={{
                border: `1px solid ${selectedOpco?.id === o.id ? '#2563eb' : '#1e293b'}`,
                backgroundColor: selectedOpco?.id === o.id ? '#1e3a5f' : '#111827',
                borderRadius: editOpcoId === o.id ? '8px 8px 0 0' : 8, padding: '9px 12px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => { setSelectedOpco(o); setEditOpcoId(null); }}>
                  <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 13 }}>{o.name}</div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                    {o.team_count} teams · {o.member_count} members{o.director_name ? ` · Dir: ${o.director_name}` : ''}
                  </div>
                </div>
                <button onClick={() => editOpcoId === o.id ? setEditOpcoId(null) : startEditOpco(o)}
                  style={btnSm(editOpcoId === o.id ? '#334155' : '#1e3a5f', editOpcoId === o.id ? '#94a3b8' : '#93c5fd', '1px solid #1d4ed855')}>
                  {editOpcoId === o.id ? 'Cancel' : '✎ Edit'}
                </button>
                {deleteOpcoConfirm === o.id ? (
                  <>
                    <button onClick={() => deleteOpco(o.id)} style={btnSm('#7f1d1d', '#fca5a5', '1px solid #b91c1c55')}>Confirm</button>
                    <button onClick={() => setDeleteOpcoConfirm(null)} style={btnSm('transparent', '#64748b', '1px solid #334155')}>✕</button>
                  </>
                ) : (
                  <button onClick={() => { setDeleteOpcoConfirm(o.id); setEditOpcoId(null); }}
                    style={btnSm('transparent', '#f87171', '1px solid #f8717144')}>🗑</button>
                )}
              </div>

              {/* Inline edit form */}
              {editOpcoId === o.id && (
                <div style={{ border: '1px solid #2563eb', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 12, backgroundColor: '#0f172a' }}>
                  <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Name</div>
                      <input value={editOpcoName} onChange={e => setEditOpcoName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveOpco()} style={inpS} autoFocus />
                    </div>
                    <div>
                      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Director</div>
                      <select value={editOpcoDir} onChange={e => setEditOpcoDir(e.target.value)} style={inpS}>
                        <option value="">— None —</option>
                        {directors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <button onClick={saveOpco} style={{ width: '100%', padding: 8, backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Teams ─────────────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 15 }}>
              Teams {selectedOpco ? `— ${selectedOpco.name}` : ''}
            </div>
            {selectedOpco && (
              <button onClick={() => { setShowNewTeam(o => !o); setEditTeamId(null); }}
                style={{ padding: '6px 12px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                + Team
              </button>
            )}
          </div>

          {/* New team form */}
          {showNewTeam && selectedOpco && (
            <div style={{ border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 12, backgroundColor: '#111827' }}>
              <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Team Name</div>
                  <input value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && createTeam()} style={inpS} autoFocus />
                </div>
                <div>
                  <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Field Manager</div>
                  <select value={newTeamFm} onChange={e => setNewTeamFm(e.target.value)} style={inpS}>
                    <option value="">— None —</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowNewTeam(false)} style={{ flex: 1, padding: 8, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                <button onClick={createTeam} style={{ flex: 2, padding: 8, backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>Create</button>
              </div>
            </div>
          )}

          {/* Team list */}
          {!selectedOpco ? (
            <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>Select an OPCO to see its teams.</div>
          ) : teams.length === 0 ? (
            <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>No teams in {selectedOpco.name} yet.</div>
          ) : teams.map(t => (
            <div key={t.id} style={{ marginBottom: 6 }}>
              {/* Team card */}
              <div style={{
                border: '1px solid #1e293b', backgroundColor: '#111827',
                borderRadius: editTeamId === t.id ? '8px 8px 0 0' : 8, padding: '9px 12px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 13 }}>{t.name}</div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                    {t.member_count} members{t.field_manager_name ? ` · FM: ${t.field_manager_name}` : ''}
                  </div>
                </div>
                <button onClick={() => editTeamId === t.id ? setEditTeamId(null) : startEditTeam(t)}
                  style={btnSm(editTeamId === t.id ? '#334155' : '#1e3a5f', editTeamId === t.id ? '#94a3b8' : '#93c5fd', '1px solid #1d4ed855')}>
                  {editTeamId === t.id ? 'Cancel' : '✎ Edit'}
                </button>
                {deleteTeamConfirm === t.id ? (
                  <>
                    <button onClick={() => deleteTeam(t.id)} style={btnSm('#7f1d1d', '#fca5a5', '1px solid #b91c1c55')}>Confirm</button>
                    <button onClick={() => setDeleteTeamConfirm(null)} style={btnSm('transparent', '#64748b', '1px solid #334155')}>✕</button>
                  </>
                ) : (
                  <button onClick={() => { setDeleteTeamConfirm(t.id); setEditTeamId(null); }}
                    style={btnSm('transparent', '#f87171', '1px solid #f8717144')}>🗑</button>
                )}
              </div>

              {/* Inline edit form */}
              {editTeamId === t.id && (
                <div style={{ border: '1px solid #166534', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 12, backgroundColor: '#0f172a' }}>
                  <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                    <div>
                      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Team Name</div>
                      <input value={editTeamName} onChange={e => setEditTeamName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveTeam()} style={inpS} autoFocus />
                    </div>
                    <div>
                      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Field Manager</div>
                      <select value={editTeamFm} onChange={e => setEditTeamFm(e.target.value)} style={inpS}>
                        <option value="">— None —</option>
                        {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <button onClick={saveTeam} style={{ width: '100%', padding: 8, backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main AdminUsers ─────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const currentUser = useUser();
  const isAdmin = currentUser?.role === 'admin';
  const canManage = ['admin', 'rpm', 'management', 'managing_director', 'director', 'field_manager'].includes(currentUser?.role);

  const [pageTab, setPageTab] = useState('team'); // 'team' | 'orgs'
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [detailTab, setDetailTab] = useState('profile');
  const [timesheet, setTimesheet] = useState([]);
  const [tsLoading, setTsLoading] = useState(false);
  const [tsFrom, setTsFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [tsTo, setTsTo] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    Promise.all([
      api('/users'),
      api('/locations').catch(() => []),
    ]).then(([userData, locationData]) => {
      const nextUsers = Array.isArray(userData) ? userData : [];
      setUsers(nextUsers);
      setLocations(Array.isArray(locationData) ? locationData : []);
      setSelectedId(nextUsers[0]?.id || null);
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
    });
  }, []);

  const filteredUsers = useMemo(() => {
    const needle = query.toLowerCase();
    return users.filter(user => {
      if (!needle) return true;
      return [user.name, user.email, user.role, user.career_level, ...(user.skills || [])]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(needle));
    });
  }, [users, query]);

  const selected = users.find(user => user.id === selectedId) || null;

  useEffect(() => {
    setEditing(selected ? normalizeUserForEdit(selected) : null);
    setTimesheet([]);
    setStatus('');
    setError('');
  }, [selectedId, selected]);

  useEffect(() => {
    setDetailTab('profile');
  }, [selectedId]);

  const loadTimesheet = () => {
    if (!selectedId) return;
    setTsLoading(true);
    api(`/time-entries/timesheet?tech_id=${selectedId}&date_from=${tsFrom}&date_to=${tsTo}`)
      .then(rows => { setTimesheet(rows); setTsLoading(false); })
      .catch(() => setTsLoading(false));
  };

  useEffect(() => {
    if (detailTab === 'timesheet' && selectedId) loadTimesheet();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, selectedId]);

  const activeCount = users.filter(user => user.is_active).length;
  const techCount = users.filter(user => user.role === 'tech').length;
  const certifiedCount = users.filter(user => Array.isArray(user.skills) && user.skills.length > 0).length;

  const saveProfile = async () => {
    if (!editing) return;
    setError('');
    setStatus('');

    const profilePayload = {
      career_level: editing.career_level || null,
      shift_start: editing.shift_start || null,
      shift_end: editing.shift_end || null,
      reporting_location_id: editing.reporting_location_id || null,
      skills: textToSkills(editing.skillsText),
      callout_count_ytd: Number(editing.callout_count_ytd || 0),
      last_callout_at: editing.last_callout_at || null,
    };

    const adminPayload = isAdmin ? {
      name: editing.name,
      email: editing.email,
      role: editing.role,
      is_active: Boolean(editing.is_active),
    } : {};

    try {
      const updated = await api(`/users/${editing.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...profilePayload, ...adminPayload }),
      });
      setUsers(prev => prev.map(user => user.id === updated.id ? updated : user));
      setEditing(normalizeUserForEdit(updated));
      setStatus('Profile saved');
    } catch (err) {
      setError(err.message);
    }
  };

  const createUser = async () => {
    setError('');
    setStatus('');
    if (!createForm.name || !createForm.email || !createForm.password) {
      setError('Name, email, and password are required');
      return;
    }
    try {
      const created = await api('/users', {
        method: 'POST',
        body: JSON.stringify({
          name: createForm.name,
          email: createForm.email,
          password: createForm.password,
          role: createForm.role,
          career_level: createForm.career_level || null,
          shift_start: createForm.shift_start || null,
          shift_end: createForm.shift_end || null,
          reporting_location_id: createForm.reporting_location_id || null,
          skills: textToSkills(createForm.skillsText),
        }),
      });
      setUsers(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedId(created.id);
      setShowCreate(false);
      setCreateForm(emptyCreateForm);
      setStatus('User created');
    } catch (err) {
      setError(err.message);
    }
  };

  const deactivate = async () => {
    if (!selected || !window.confirm(`Deactivate ${selected.name}?`)) return;
    setError('');
    try {
      await api(`/users/${selected.id}`, { method: 'DELETE' });
      setUsers(prev => prev.map(user => user.id === selected.id ? { ...user, is_active: false } : user));
      setStatus('User deactivated');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!canManage) {
    return (
      <div style={{ flex: 1, backgroundColor: '#0f172a', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
        My Team is available to admin, RPM, and management roles.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ flex: 1, backgroundColor: '#0f172a', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
        Loading team...
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: 'hidden', backgroundColor: '#0f172a', fontFamily: 'sans-serif', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
      <div style={{ borderBottom: '1px solid #1e293b', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 20 }}>My Team</div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>
            {activeCount} active · {techCount} techs · {certifiedCount} with skills
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['team', 'orgs'].map(k => (
            <button key={k} onClick={() => setPageTab(k)} style={{
              padding: '8px 14px', borderRadius: 6, border: '1px solid #334155', cursor: 'pointer', fontSize: 13, fontWeight: 700,
              backgroundColor: pageTab === k ? '#1e3a5f' : 'transparent',
              color: pageTab === k ? '#93c5fd' : '#64748b',
            }}>{k === 'team' ? 'Team' : 'OPCOs & Teams'}</button>
          ))}
          {isAdmin && pageTab === 'team' && (
            <button onClick={() => setShowCreate(true)} style={{ padding: '9px 14px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 800 }}>
              Add User
            </button>
          )}
        </div>
      </div>

      {pageTab === 'orgs' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <OrgAdmin users={users} />
        </div>
      )}

      {pageTab === 'team' && <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', minHeight: 0, flex: 1 }}>
        <aside style={{ borderRight: '1px solid #1e293b', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 14, borderBottom: '1px solid #1e293b' }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search team"
              style={{ ...fieldStyle(), padding: '9px 10px' }}
            />
          </div>
          <div style={{ overflowY: 'auto', minHeight: 0 }}>
            {filteredUsers.map(user => {
              const selectedRow = user.id === selectedId;
              return (
                <button
                  key={user.id}
                  onClick={() => setSelectedId(user.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    borderBottom: '1px solid #111827',
                    backgroundColor: selectedRow ? '#1e3a5f' : '#0f172a',
                    color: '#e2e8f0',
                    padding: '13px 16px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ fontWeight: 800, color: user.is_active ? '#f8fafc' : '#64748b', fontSize: 14 }}>{user.name}</div>
                    <div style={{ color: user.is_active ? '#22c55e' : '#ef4444', fontSize: 11, fontWeight: 800 }}>{user.is_active ? 'Active' : 'Inactive'}</div>
                  </div>
                  <div style={{ color: ROLE_COLORS[user.role] || '#94a3b8', fontSize: 12, fontWeight: 700, marginTop: 4 }}>
                    {ROLE_LABELS[user.role] || user.role}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>
                    Career: {formatCareer(user.career_level)} · {formatTime(user.shift_start)}-{formatTime(user.shift_end)}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <main style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!editing ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>Select an employee.</div>
          ) : (
            <>
            <div style={{ borderBottom: '1px solid #1e293b', display: 'flex', padding: '0 20px' }}>
              {[{ key: 'profile', label: 'Profile' }, { key: 'timesheet', label: 'Timesheet' }, { key: 'callout_history', label: 'Callout History' }, { key: 'org', label: 'Org / PIN' }].map(t => (
                <button key={t.key} onClick={() => setDetailTab(t.key)} style={{
                  padding: '12px 16px', border: 'none',
                  borderBottom: detailTab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: detailTab === t.key ? '#93c5fd' : '#64748b',
                  cursor: 'pointer', fontSize: 13,
                  fontWeight: detailTab === t.key ? 800 : 600,
                }}>{t.label}</button>
              ))}
            </div>
            {detailTab === 'org' ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
                <OrgUserPanel user={editing} locations={locations} onSaved={updated => setUsers(prev => prev.map(u => u.id === updated.id ? updated : u))} />
              </div>
            ) : detailTab === 'callout_history' ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
                <CalloutHistoryPanel user={editing} />
              </div>
            ) : detailTab === 'timesheet' ? (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
                <TimesheetPanel
                  user={editing}
                  timesheet={timesheet}
                  loading={tsLoading}
                  dateFrom={tsFrom}
                  dateTo={tsTo}
                  onFromChange={v => setTsFrom(v)}
                  onToChange={v => setTsTo(v)}
                  onLoad={loadTimesheet}
                />
              </div>
            ) : (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>
            <div style={{ maxWidth: 980 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 12 }}>
                <div>
                  <div style={{ color: '#f8fafc', fontSize: 22, fontWeight: 800 }}>{editing.name}</div>
                  <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{editing.email}</div>
                </div>
                {isAdmin && editing.is_active && (
                  <button onClick={deactivate} style={{ padding: '8px 12px', backgroundColor: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                    Deactivate
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: 10, marginBottom: 18 }}>
                {[
                  ['Career', formatCareer(editing.career_level)],
                  ['Shift', `${formatTime(editing.shift_start)}-${formatTime(editing.shift_end)}`],
                  ['Skills', textToSkills(editing.skillsText).length],
                  ['Callouts YTD', editing.callout_count_ytd || 0],
                ].map(([label, value]) => (
                  <div key={label} style={{ backgroundColor: '#111827', border: '1px solid #1e293b', borderRadius: 7, padding: 12 }}>
                    <div style={{ color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 800, marginTop: 5 }}>{value}</div>
                  </div>
                ))}
              </div>

              <section style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ padding: '10px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800 }}>Account</div>
                <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Name">
                    <input disabled={!isAdmin} value={editing.name || ''} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} style={fieldStyle(!isAdmin)} />
                  </Field>
                  <Field label="Email">
                    <input disabled={!isAdmin} value={editing.email || ''} onChange={e => setEditing(p => ({ ...p, email: e.target.value }))} style={fieldStyle(!isAdmin)} />
                  </Field>
                  <Field label="Role">
                    <select disabled={!isAdmin} value={editing.role || 'tech'} onChange={e => setEditing(p => ({ ...p, role: e.target.value }))} style={fieldStyle(!isAdmin)}>
                      {ROLES.map(role => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
                    </select>
                  </Field>
                  <Field label="Active">
                    <select disabled={!isAdmin} value={editing.is_active ? 'true' : 'false'} onChange={e => setEditing(p => ({ ...p, is_active: e.target.value === 'true' }))} style={fieldStyle(!isAdmin)}>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </Field>
                </div>
              </section>

              <section style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ padding: '10px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800 }}>Field Profile</div>
                <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Career Level">
                    <select value={editing.career_level || ''} onChange={e => setEditing(p => ({ ...p, career_level: e.target.value }))} style={fieldStyle()}>
                      {CAREER_LEVELS.map(level => <option key={level.value} value={level.value}>{level.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Reporting Location">
                    <select value={editing.reporting_location_id || ''} onChange={e => setEditing(p => ({ ...p, reporting_location_id: e.target.value }))} style={fieldStyle()}>
                      <option value="">Unassigned</option>
                      {locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Shift Start">
                    <input type="time" value={editing.shift_start || ''} onChange={e => setEditing(p => ({ ...p, shift_start: e.target.value }))} style={fieldStyle()} />
                  </Field>
                  <Field label="Shift End">
                    <input type="time" value={editing.shift_end || ''} onChange={e => setEditing(p => ({ ...p, shift_end: e.target.value }))} style={fieldStyle()} />
                  </Field>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Skills">
                      <input value={editing.skillsText || ''} onChange={e => setEditing(p => ({ ...p, skillsText: e.target.value }))} placeholder="transport, fiber_splice, radio_repair" style={fieldStyle()} />
                    </Field>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {DEFAULT_SKILLS.map(skill => (
                        <button
                          key={skill}
                          onClick={() => {
                            const skills = new Set(textToSkills(editing.skillsText));
                            skills.has(skill) ? skills.delete(skill) : skills.add(skill);
                            setEditing(p => ({ ...p, skillsText: Array.from(skills).join(', ') }));
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 5,
                            border: '1px solid #334155',
                            backgroundColor: textToSkills(editing.skillsText).includes(skill) ? '#1e3a5f' : '#0f172a',
                            color: textToSkills(editing.skillsText).includes(skill) ? '#93c5fd' : '#94a3b8',
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          {skill}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ padding: '10px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800 }}>Callout Tracking</div>
                <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Callouts YTD">
                    <input type="number" min="0" value={editing.callout_count_ytd || 0} onChange={e => setEditing(p => ({ ...p, callout_count_ytd: e.target.value }))} style={fieldStyle()} />
                  </Field>
                  <Field label="Last Callout">
                    <input type="datetime-local" value={editing.last_callout_at ? String(editing.last_callout_at).slice(0, 16) : ''} onChange={e => setEditing(p => ({ ...p, last_callout_at: e.target.value }))} style={fieldStyle()} />
                  </Field>
                </div>
              </section>

              {(error || status) && (
                <div style={{ color: error ? '#f87171' : '#22c55e', marginBottom: 12, fontSize: 13, fontWeight: 700 }}>
                  {error || status}
                </div>
              )}
              <button onClick={saveProfile} style={{ padding: '10px 18px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>
                Save Profile
              </button>
            </div>
            </div>
            )}
            </>
          )}
        </main>
      </div>}

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.72)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#1e293b', borderRadius: 8, border: '1px solid #334155', width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto', padding: 20 }}>
            <div style={{ color: '#f8fafc', fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Add User</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Name"><input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} style={fieldStyle()} /></Field>
              <Field label="Email"><input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} style={fieldStyle()} /></Field>
              <Field label="Temporary Password"><input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} style={fieldStyle()} /></Field>
              <Field label="Role">
                <select value={createForm.role} onChange={e => setCreateForm(p => ({ ...p, role: e.target.value }))} style={fieldStyle()}>
                  {ROLES.map(role => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
                </select>
              </Field>
              <Field label="Career Level">
                <select value={createForm.career_level} onChange={e => setCreateForm(p => ({ ...p, career_level: e.target.value }))} style={fieldStyle()}>
                  {CAREER_LEVELS.map(level => <option key={level.value} value={level.value}>{level.label}</option>)}
                </select>
              </Field>
              <Field label="Reporting Location">
                <select value={createForm.reporting_location_id} onChange={e => setCreateForm(p => ({ ...p, reporting_location_id: e.target.value }))} style={fieldStyle()}>
                  <option value="">Unassigned</option>
                  {locations.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
                </select>
              </Field>
              <Field label="Shift Start"><input type="time" value={createForm.shift_start} onChange={e => setCreateForm(p => ({ ...p, shift_start: e.target.value }))} style={fieldStyle()} /></Field>
              <Field label="Shift End"><input type="time" value={createForm.shift_end} onChange={e => setCreateForm(p => ({ ...p, shift_end: e.target.value }))} style={fieldStyle()} /></Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Skills">
                  <input value={createForm.skillsText} onChange={e => setCreateForm(p => ({ ...p, skillsText: e.target.value }))} placeholder="transport, fiber_splice" style={fieldStyle()} />
                </Field>
              </div>
            </div>
            {error && <div style={{ color: '#f87171', marginTop: 12, fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button onClick={() => { setShowCreate(false); setError(''); }} style={{ flex: 1, padding: 10, backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={createUser} style={{ flex: 2, padding: 10, backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>Create User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
